'use client';

import Typography from '../../../atoms/Typography/Typography';
import Iconography from '../../../atoms/Iconography/Iconography';
import Checkbox from '../../../atoms/Checkbox/Checkbox';
import Box from '../../../atoms/Box/Box';
import { TableColumn } from '../types';
import { getColumnStyle, getAlignClass } from '../utils';
import styles from './Row.module.scss';

type RowProps<T> = {
  record: T;
  rowIndex: number;
  actualIndex: number;
  columns: TableColumn<T>[];
  theme: 'light' | 'dark';
  selectable: boolean;
  isSelected: boolean;
  onRowSelect: (index: number, checked: boolean) => void;
  expandable: boolean;
  isExpanded: boolean;
  onToggleExpand: (index: number) => void;
};

/**
 * Table row component that renders a single data row with cells.
 *
 * @component
 * @param {RowProps<T>} props - Component props
 * @param {T} props.record - The data record for this row
 * @param {number} props.rowIndex - Index of the row in the current page
 * @param {number} props.actualIndex - Actual index in the full dataset
 * @param {TableColumn<T>[]} props.columns - Column configuration array
 * @param {'light' | 'dark'} props.theme - Theme variant
 * @param {boolean} props.selectable - Whether row selection is enabled
 * @param {boolean} props.isSelected - Whether this row is selected
 * @param {function} props.onRowSelect - Callback when row selection changes
 * @returns {React.ReactElement} The Row component
 */
const Row = <T extends Record<string, unknown>>({
  record,
  rowIndex,
  actualIndex,
  columns,
  theme,
  selectable,
  isSelected,
  onRowSelect,
  expandable,
  isExpanded,
  onToggleExpand,
}: RowProps<T>) => {
  return (
    <tr
      className={`${styles.tableRow} ${isSelected ? styles.selectedRow : ''} ${expandable ? styles.clickableRow : ''} ${isExpanded ? styles.expandedRow : ''}`}
      onClick={expandable ? () => onToggleExpand(actualIndex) : undefined}
    >
      <td className={`${styles.tableCell} ${styles.rowNumberCell}`}>
        <Typography variant="body" size="small" theme={theme} color="white">
          {actualIndex + 1}
        </Typography>
      </td>
      {selectable && (
        <td
          className={`${styles.tableCell} ${styles.checkboxCell}`}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <Box className={styles.checkboxWrapper}>
            <Checkbox
              isChecked={isSelected}
              handleChange={(checked) => onRowSelect(actualIndex, checked)}
              dataTestId={`table-row-${actualIndex}`}
              gaComponent="table-row-select"
              theme={theme}
              logEvents={false}
              showValidation={false}
            />
          </Box>
        </td>
      )}
      {columns.map((column) => (
        <td
          key={column.key}
          className={styles.tableCell}
          // eslint-disable-next-line react/forbid-dom-props
          style={getColumnStyle(column)}
        >
          <Box className={`${styles.cellContent} ${getAlignClass(column.align, styles)}`}>
            {column.render ? (
              column.render(record[column.key], record, rowIndex)
            ) : (
              <Typography variant="body" size="small" theme={theme} color="white">
                {String(record[column.key] ?? '')}
              </Typography>
            )}
          </Box>
        </td>
      ))}
      {expandable && (
        <td
          className={`${styles.tableCell} ${styles.expandCell}`}
          data-testid={`table-row-expand-${actualIndex}`}
        >
          <span className={`${styles.expandIcon} ${isExpanded ? styles.expandIconRotated : ''}`}>
            <Iconography category="arrows" name="chevron-down" color="white" theme={theme} />
          </span>
        </td>
      )}
    </tr>
  );
};

export default Row;
